import React, { useRef } from 'react';
import moment from 'moment';
import { capitalize } from 'lodash';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { ItemTypes } from './ItemTypes';

const style = {
  cursor: 'move',
};
function CustomCardDrag({ column, index, redirectEditPage, toggleDeleteModal, moveTr, id }) {
  const { t } = useTranslation('common');

  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveTr(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0.5 : 1;
  drag(drop(ref));

  return (
    <tr key={id} style={{ ...style, opacity }} data-handler-id={handlerId} ref={ref}>
      <td>{column?.title || '--'}</td>
      <td>{capitalize(column?.columnType) || 'Manual'}</td>
      <td>{column?.type ? column?.type?.replaceAll('_', ' ') : '--'}</td>

      <td>
        <div className="table-action-container">
          <button
            onClick={() => redirectEditPage(column)}
            className="btn p-0 d-inline-flex"
            type="button"
            aria-label={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.EDIT_BUTTON')}
            title={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.EDIT_BUTTON')}
          >
            <span className="icon-Edit-1 f-20" />
          </button>
          <button
            type="button"
            aria-label={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.DRAG_BUTTON')}
            title={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.DRAG_BUTTON')}
            className="btn p-0 d-inline-flex"
            id={column?._id}
          >
            <span className="icon-expand" />
          </button>
          <button
            type="button"
            onClick={() => toggleDeleteModal(true, column?._id)}
            className="btn p-0 d-inline-flex"
            title={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.DELETE_BUTTON')}
            aria-label={t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.DELETE_BUTTON')}
          >
            <span className="icon-delete" />
          </button>
          <div className="info-container d-flex text-left ml-16">
            <span className="icon-Information  f-20" />
            <div className="info-content">
              <p className="mb-1">
                <b>{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.CREATED_LABEL')}&nbsp;</b>
                {column?.createdAt ? moment(column?.createdAt).format('DD-MMMM-YYYY HH:mm') : '--'}
              </p>
              <p className="mb-1">
                <b>{t('RANGE_PLANS.CUSTOM_SECTION.ADD_COLUMNS.CUSTUM_CARD_DRAG.MODIFIED_LABEL')}&nbsp;</b>{' '}
                {` ${column?.modifiedAt && moment(column?.modifiedAt).format('DD-MMMM-YYYY HH:mm')}`}
              </p>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default CustomCardDrag;
